import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { cosmetics as pageCosmetics } from '../../cosmetics'
import type { Cosmetics } from '../../cosmetics'
import type { NetState } from '../../net/GameClient'
import type { Flow, JoinKind } from '../flow'
import { phase2Slots, useMaybeStore } from '../hub/slots'
import type { RoomInfo, Session } from '../session'
import { useStore } from '../useStore'
import { seatRunner, uiArt } from './art'
import { CopyCode } from './CopyCode'
import { EmoteButtons } from './EmoteButtons'
import styles from './matchmaking.module.css'
import { Pedestal } from './Pedestal'
import { TIP_MS, TIPS, tipAt } from './tips'

const SEATS = 4
const BG_ART = uiArt('ui_bg_hub')

const JOINING_TITLE: Record<JoinKind, string> = {
  public: 'Finding racers…',
  private: 'Opening your room…',
  code: 'Joining the room…',
}

interface Props {
  session: Session
  flow: Flow
  room: RoomInfo | null
  net: NetState
  emotes: (number | null)[]
  /**
   * `lobby` (waiting, joining) or the race screen's first moments (`handoff`, see handoff.ts):
   * then the title reads "Get ready!" and Back, Start and the emotes are gone.
   */
  mode?: 'lobby' | 'handoff'
  /** The race scene is still being built: "Loading…" over the pedestals. */
  loading?: boolean
  /** Injected by tests; the page's store otherwise. */
  cosmetics?: Cosmetics
}

/** One tip every `TIP_MS`, starting at a different tip on each visit. */
function useTip(): number {
  const [start] = useState(() => (Math.floor(Date.now() / TIP_MS) % TIPS.length) * TIP_MS)
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const t0 = Date.now()
    const id = setInterval(() => setElapsed(Date.now() - t0), 1000)
    return () => clearInterval(id)
  }, [])
  return tipAt(start + elapsed)
}

function BackArrow() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <path
        d="M15 5l-7 7 7 7"
        fill="none"
        stroke="currentColor"
        stroke-width="3.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  )
}

function subtitle(net: NetState, ready: boolean, isPrivate: boolean, isHost: boolean): string {
  if (net.status === 'offline') return 'Connection trouble, retrying…'
  if (!ready) return 'Hold on, looking for a seat.'
  if (!isPrivate) return 'Empty seats get bots when the race starts.'
  return isHost ? 'Empty seats get bots when you start.' : 'Waiting for the host…'
}

/**
 * The matchmaking screen (PHASE3 T3.2, D-048): four tinted pedestals that fill left to right as
 * racers join, a rotating tip, Back (and Escape) to the hub, the emote buttons. Private rooms
 * show the code top-centre and the host's Start race; guests see "Waiting for the host…".
 * The countdown plays on the race screen; `handoff` keeps this screen up for its first moment.
 */
export function Matchmaking({
  session,
  flow,
  room,
  net,
  emotes,
  mode = 'lobby',
  loading = false,
  cosmetics = pageCosmetics,
}: Props) {
  const slots = useMemo(() => phase2Slots(session), [session])
  const profile = useMaybeStore(slots.profile)
  const myCharId = useStore(cosmetics.charId)
  const tip = useTip()
  const start = useRef<HTMLButtonElement>(null)
  const handoff = mode === 'handoff'
  const ready = !flow.joining && room !== null
  const isPrivate = ready && room.code !== ''
  const isHost = ready && room.isHost

  useEffect(() => {
    // The host lands on Start. Nothing else takes focus: a stray Enter must not press Back.
    start.current?.focus()
  }, [ready, isHost])

  useEffect(() => {
    if (handoff) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      session.toMenu()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handoff, session])

  const title = handoff
    ? 'Get ready!'
    : !ready
      ? JOINING_TITLE[flow.joining ?? 'public']
      : room.phase === 'countdown'
        ? 'Get ready!'
        : 'Waiting for racers'
  const seats = ready ? room.seats : []
  const silhouette = seatRunner(cosmetics.runners, myCharId)

  return (
    <div class={`${styles.screen} ${handoff ? styles.handoff : ''}`}>
      <div
        class={styles.backdrop}
        style={BG_ART ? { backgroundImage: `url('${BG_ART}')` } : undefined}
        aria-hidden="true"
      />
      <header class={styles.top}>
        {!handoff && (
          <button
            type="button"
            class={styles.back}
            aria-label="Back"
            onClick={() => session.toMenu()}
          >
            <BackArrow />
          </button>
        )}
        <div class={styles.heading}>
          <h2 class={styles.title} aria-live="polite">
            {title}
          </h2>
          {!handoff && <p class={styles.subtitle}>{subtitle(net, ready, isPrivate, isHost)}</p>}
        </div>
        {!handoff && isHost && isPrivate && (
          <button ref={start} type="button" class={styles.start} onClick={() => session.start()}>
            Start race
          </button>
        )}
      </header>
      {isPrivate && !handoff && (
        <div class={styles.codeBox}>
          <span class={styles.codeLabel}>Room code</span>
          <CopyCode code={room.code} link={session.shareLink(room.code)} class={styles.code} />
        </div>
      )}
      <ul class={styles.row} aria-label="Racers">
        {Array.from({ length: SEATS }, (_, i) => {
          const seat = seats.find((s) => s.index === i)
          return (
            <Pedestal
              key={i}
              index={i}
              seat={seat}
              runner={seat ? seatRunner(cosmetics.runners, seat.charId) : silhouette}
              level={seat?.isMe && typeof profile?.level === 'number' ? profile.level : null}
              emote={seat ? (emotes[i] ?? null) : null}
            />
          )
        })}
      </ul>
      {loading && (
        <div class={styles.loading} role="status">
          Loading…
        </div>
      )}
      <footer class={styles.bottom}>
        <p key={tip} class={styles.tip}>
          <span class={styles.tipLabel}>Tip</span> {TIPS[tip]}
        </p>
        {!handoff && ready && (
          <EmoteButtons class={styles.emotes} onEmote={(id) => session.emote(id)} />
        )}
      </footer>
    </div>
  )
}
